import { OrganizationLicenseUsage, LicenseAction } from 'types/schema-generated-types';
import { aggregateUsages } from 'utilities/product-utils';
import { LicenseUpdate } from '../StudentProductLicenseCard';

export interface PendingUpdateResult {
  canAssign: boolean;
  siteLicensed: boolean;
  willUseOverage: boolean;
  showAvailableCount: boolean;
  realAvailableCountAfterUpdate: number;
  potentialAvailableCountAfterUpdate: number;
}

const getCanAssign = (
  siteLicensed: boolean,
  isOverageAvailable: boolean,
  realAvailableCount: number,
  potentialAvailableCount: number,
  pendingUsageCount: number
) => {
  if (siteLicensed) return true; // Can always assign if site-licensed.
  if (pendingUsageCount === 0) {
    return isOverageAvailable ? potentialAvailableCount > 0 : realAvailableCount > 0;
  }

  const realAvailableCountAfterUpdate = realAvailableCount - pendingUsageCount;
  if (!isOverageAvailable) return realAvailableCountAfterUpdate > 0;

  const potentialAvailableCountAfterUpdate = potentialAvailableCount - pendingUsageCount;
  return potentialAvailableCountAfterUpdate > 0;
};

const getPendingUpdateResult = (
  usages: OrganizationLicenseUsage[],
  pendingUpdates: LicenseUpdate[]
): PendingUpdateResult => {
  // Calculate the number of licenses that will be used based on the pending updates.
  // Assign actions subtract from available count, while unassigning adds to it.
  const pendingUsageCount =
    pendingUpdates.filter(x => x.action === LicenseAction.ASSIGN).length -
    pendingUpdates.filter(x => x.action === LicenseAction.UNASSIGN).length;

  const {
    siteLicensed,
    realAvailableCount,
    realTotalCount,
    potentialAvailableCount,
    potentialTotalCount
  } = aggregateUsages(usages);

  const isOverageAvailable = potentialTotalCount > realTotalCount;
  const realAvailableCountAfterUpdate = realAvailableCount - pendingUsageCount;

  const willUseOverage = isOverageAvailable && realAvailableCountAfterUpdate <= 0;
  const potentialAvailableCountAfterUpdate = potentialAvailableCount - pendingUsageCount;

  const canAssign = getCanAssign(
    siteLicensed,
    isOverageAvailable,
    realAvailableCount,
    potentialAvailableCount,
    pendingUsageCount
  );

  const showAvailableCount =
    !siteLicensed &&
    canAssign &&
    (realAvailableCountAfterUpdate > 0 || potentialAvailableCountAfterUpdate > 0);

  return {
    canAssign,
    siteLicensed,
    willUseOverage,
    showAvailableCount,
    realAvailableCountAfterUpdate,
    potentialAvailableCountAfterUpdate
  };
};

export default getPendingUpdateResult;
